// reducer - state management
import { LOGIN, LOGOUT } from 'store/reducers/actions';
import { clearAuthData, isTokenExpired, isValidUserData } from 'utils/tokenUtils';
import { createContext, useEffect, useReducer } from 'react';

// third-party
// import { Chance } from 'chance';
// project-imports
import Loader from 'components/Loader';
import PropTypes from 'prop-types';
import authReducer from 'store/reducers/auth';
import axios from 'utils/axios';

// const chance = new Chance();

// constant
const initialState = {
  isLoggedIn: false,
  isInitialized: false,
  user: null,
  token: null
};

const setSession = (token) => {
  if (token) {
    localStorage.setItem('token', token);
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common.Authorization;
  }
};

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const BUSContext = createContext(null);

export const JWTProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const init = async () => {
      try {
        const token = localStorage.getItem('token');
        const userStr = localStorage.getItem('user');

        if (token && userStr) {
          const user = JSON.parse(userStr);

          // Validate user data
          if (!isValidUserData(user)) {
            clearAuthData();
            dispatch({
              type: LOGOUT
            });
            return;
          }

          // Check if token is expired
          if (user.expires_at && isTokenExpired(user.expires_at)) {
            // Token expired, clear everything
            clearAuthData();
            dispatch({
              type: LOGOUT
            });
            return;
          }

          // Token is valid, set session and dispatch login
          setSession(token);
          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user: user,
              token: token
            }
          });
        } else {
          // No token or user data, ensure clean state
          setSession(null);
          dispatch({
            type: LOGOUT
          });
        }
      } catch (err) {
        console.error('Initialization error:', err);
        // Clear everything on error
        clearAuthData();
        dispatch({
          type: LOGOUT
        });
      }
    };
    init();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post('/login', { email, password });
      const { token, user } = response.data;

      // Store user data with expiration
      const userWithExpiration = {
        ...user,
        expires_at: user.expires_at || null
      };

      setSession(token);
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userWithExpiration));

      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user: userWithExpiration,
          token: token
        }
      });

      return response;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const register = async ({ first_name, last_name, phone, email, password, password_confirmation }) => {
    try {
      const response = await axios.post('/register', {
        first_name,
        last_name,
        phone,
        email,
        password,
        password_confirmation
      });

      return response;
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Try to call logout endpoint if user is logged in
      if (state.token) {
        await axios.post('/logout');
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
      // Continue with local logout even if API call fails
    } finally {
      // Always clear local state
      clearAuthData();
      dispatch({ type: LOGOUT });
    }
  };

  const changeEmail = async (new_email, password) => {
    try {
      const response = await axios.put(`/change_email`, { new_email, password });

      // Update user data if email change was successful
      if (response.data.user) {
        const updatedUser = { ...state.user, ...response.data.user };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        dispatch({
          type: LOGIN,
          payload: {
            isLoggedIn: true,
            user: updatedUser,
            token: state.token
          }
        });
      }

      return response;
    } catch (error) {
      console.error('Email change failed:', error);
      throw error;
    }
  };

  const changePassword = async (old_password, new_password) => {
    try {
      const response = await axios.put(`/change_password`, { old_password, new_password });
      return response;
    } catch (error) {
      console.error('Password change failed:', error);
      throw error;
    }
  };

  const forgotPassword = async (email) => {
    try {
      const response = await axios.post(`/forgot-password`, { email });
      return response;
    } catch (error) {
      console.error('Forgot password failed:', error);
      throw error;
    }
  };

  const resetPassword = async (token, email, password, password_confirmation) => {
    try {
      const response = await axios.post(`/reset-password`, {
        token,
        email,
        password,
        password_confirmation
      });
      return response;
    } catch (error) {
      console.error('Password reset failed:', error);
      throw error;
    }
  };

  // Check token expiration periodically
  useEffect(() => {
    if (state.isLoggedIn && state.user?.expires_at) {
      const checkExpiration = () => {
        if (isTokenExpired(state.user.expires_at)) {
          console.log('Token expired, logging out...');
          logout();
        }
      };

      // Check every minute
      const interval = setInterval(checkExpiration, 60000);
      return () => clearInterval(interval);
    }
  }, [state.isLoggedIn, state.user?.expires_at, logout]);

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return (
    <BUSContext.Provider
      value={{
        ...state,
        login,
        logout,
        register,
        changeEmail,
        changePassword,
        forgotPassword,
        resetPassword
      }}
    >
      {children}
    </BUSContext.Provider>
  );
};

JWTProvider.propTypes = {
  children: PropTypes.node
};

export default BUSContext;
